<template>
    <v-card class="mb-6">
        <v-card-title>{{ props.label }}</v-card-title>
        <v-card-subtitle v-if="props.hint && props.hint !== props.label">
            {{ props.hint }}
        </v-card-subtitle>

        <v-card-text>
            <draggable :list="items"
                       :item-key="((item) => items.indexOf(item))"
                       handle=".drag-handle">
                <template #item="{element, index}">
                    <media-field :store-name="props.storeName"
                                 :json_path="`${props.json_path}[${index}]`"
                                 :label="`${props.label} ${index + 1}`"
                                 :component_config="props.component_config"
                                 :vuetify_config="props.vuetify_config"
                                 :repeatable-child="true"
                                 @deleted="handleDeleted" />
                </template>
            </draggable>
        </v-card-text>

        <v-card-actions>
            <v-btn icon="mdi-plus-circle"
                   :title="$t('editor.fields.list.add_item')"
                   @click="addToList"
                   color="primary"
                   class="mx-auto" />
        </v-card-actions>
    </v-card>
</template>

<script setup>
import {computed} from "vue";
import draggable from "vuedraggable";
import MediaField from "./MediaField";
import jp from "jsonpath";
import {mandatory_props} from "../../utils/editorConfigFieldHelpers";
import {isStepDraftStore} from "../../utils/editorFieldPersistence.mjs";
import {getStoreByName} from "../../utils/storeHelpers";
import {useCampaignStore} from "../../Stores/CampaignStore";

const props = defineProps(mandatory_props);
const store = getStoreByName(props.storeName);
const campaignStore = useCampaignStore();

const items = computed({
    get() {
        return jp.value(store[props.jsonKey], props.json_path) || [];
    },
    set(v) {
        jp.value(store[props.jsonKey], props.json_path, v);
    }
});

function addToList() {
    const list = [...items.value];

    list.push({
        type: "image",
        url: ""
    });

    items.value = list;
}

function removeFromList(index) {
    const list = [...items.value];

    list.splice(index, 1);

    items.value = list;
}

function getCampaignFieldFromJsonPath() {
    const parsed = jp.parse(props.json_path);

    return parsed[2]?.expression?.value || null;
}

function getComponentEditorBaseRevision(field) {
    if (props.storeName !== 'ComponentEditStore') {
        return null;
    }

    if (store.editingRevisionField !== field) {
        return null;
    }

    return store.baseRevision;
}

function syncComponentEditorRevision(responseData, field) {
    if (props.storeName !== 'ComponentEditStore') {
        return;
    }

    if (typeof store.syncBaseRevisionFromCampaignResponse === 'function') {
        store.syncBaseRevisionFromCampaignResponse(responseData, field);
    }
}

function persistRepeaterChange() {
    if (isStepDraftStore(props.storeName)) {
        return Promise.resolve();
    }

    const field = getCampaignFieldFromJsonPath();

    if (field === 'components') {
        return campaignStore
            .updateComponents(getComponentEditorBaseRevision('components'))
            .then(response => {
                syncComponentEditorRevision(response, 'components');

                return response;
            });
    }

    if (field === 'meta') {
        return campaignStore
            .updateMeta(getComponentEditorBaseRevision('meta'))
            .then(response => {
                syncComponentEditorRevision(response, 'meta');

                return response;
            });
    }

    if (typeof store.save === 'function') {
        return store.save();
    }

    return Promise.resolve();
}

function handleDeleted(json_path) {
    const index = Number(jp.parse(json_path).pop().expression.value);

    removeFromList(index);

    persistRepeaterChange().catch(error => {
        if (campaignStore.handleExpiredEditorSession(error)) {
            return;
        }

        if (campaignStore.handleStaleRevision(error)) {
            return;
        }

        console.error('Media repeater delete failed:', error); // i18n-ignore (Konsole)
    });
}
</script>

<style scoped>

</style>
